import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { toast } from 'sonner';
import { Eye, Presentation, Building2, UserRound, ArrowLeft } from 'lucide-react';

const META = {
  demo: { label: 'Demo account', hint: 'Prospect sandbox with sample analytics', icon: Presentation },
  customer: { label: 'Customer account', hint: 'Read-only customer portal', icon: Building2 },
  associate: { label: 'Regular associate', hint: 'Training, badges, certificates', icon: UserRound },
};

export const ShowcaseSwitcher = () => {
  const { viewAs } = useAuth();
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { api.get('/admin/showcase-accounts').then((r) => setAccounts(r.data)).catch(() => {}); }, []);
  const open = async (a) => {
    try { await viewAs(a.id); window.location.assign('/'); } catch (e) { toast.error(e.response?.data?.detail || 'Could not open showcase view'); }
  };
  if (!accounts.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="border-[#F37021]/40 text-[#F37021] hover:bg-[#F37021]/10 hover:text-[#F37021] gap-1.5 px-2.5" data-testid="view-as-btn">
          <Eye size={15} /><span className="hidden sm:inline text-xs font-semibold">View as</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid="view-as-menu">
        <DropdownMenuLabel className="text-xs text-slate-500 font-normal">Showcase as (view only, sample data)</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {accounts.map((a) => {
          const m = META[a.role] || META.associate;
          return (
            <DropdownMenuItem key={a.id} onClick={() => open(a)} className="gap-3 py-2.5 cursor-pointer" data-testid={`view-as-${a.role}`}>
              <div className="w-8 h-8 rounded-lg bg-[#002B47]/5 flex items-center justify-center shrink-0"><m.icon size={16} className="text-[#002B47]" /></div>
              <div className="min-w-0"><p className="text-sm font-medium text-slate-800">{m.label}</p><p className="text-xs text-slate-500 truncate">{a.name} · {m.hint}</p></div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ShowcaseBanner = () => {
  const { user, returnToAdmin } = useAuth();
  if (!user?.impersonated_by) return null;
  const back = () => { returnToAdmin(); window.location.assign('/admin'); };
  return (
    <div className="sticky top-0 z-40 bg-[#F37021] text-white px-4 py-2 flex items-center justify-between gap-3 text-sm" data-testid="showcase-banner">
      <p className="flex items-center gap-2 min-w-0"><Eye size={15} className="shrink-0" />
        <span className="truncate">Viewing as <b>{user.name}</b> ({META[user.role]?.label || user.role}) · view only</span></p>
      <Button size="sm" onClick={back} className="bg-white text-[#F37021] hover:bg-white/90 h-7 rounded-full shrink-0 gap-1" data-testid="return-to-admin-btn">
        <ArrowLeft size={14} /> Back to admin
      </Button>
    </div>
  );
};
